/**
 * The header cell class for every list table.
 *
 * WHY min-w-0 truncate IS LOAD-BEARING, not styling:
 *
 * Each list page draws its header row and its data rows as two SEPARATE grid elements that happen
 * to carry the same template. Two grids resolve their track sizes independently, and an `fr` track
 * cannot shrink below its content's min-content width unless that content's overflow is hidden. The
 * header spans overflowed visible, so every header track was forced at least as wide as its longest
 * word - "CUSTOMER", "SUBMITTED", "PROVIDER" - while the data cells, which mostly carry `truncate`,
 * had an automatic minimum of 0. The two grids therefore resolved to DIFFERENT widths and every
 * heading sat progressively further right than the values beneath it.
 *
 * It was invisible at a wide window and obvious at a narrow one: measured on the orders table, the
 * drift was 0px at 1800px wide, 13px at 1400px, 19px at 1280px and 28px at 1100px.
 *
 * Pair this with a template whose flexible tracks are minmax(<floor>, Nfr) and whose action column
 * is a fixed px width. Then both grids resolve identically whatever is inside them.
 *
 * HOW THE fr NUMBERS WERE CHOSEN, because they look arbitrary and are not:
 *
 * Alignment was only half the problem. The columns were also spaced unevenly - on the orders list
 * the gap between a value and the next heading ran from 14px to 250px, an 18x spread, because the
 * flexible columns hoarded every pixel of slack while the fixed ones sat at their minimum. It read
 * as an accident, which is exactly what it was.
 *
 * So each column's natural width was MEASURED in a browser - the wider of its heading and its widest
 * cell, over representative rows - and its flex share set to (natural + 140) / 100. The constant is
 * what evens the rhythm: share proportional to natural alone gives a wide column a wide gap, and a
 * constant alone gives every column the same width regardless of content. 140 was picked by sweeping
 * it across 8 tables x 5 viewport widths and taking the value with the lowest worst-case gap spread.
 * Standard deviation of the gaps fell from 170 to 29 worst-case, 52 to 14 on average, at a cost of
 * one extra truncated cell in 120.
 *
 * The floor in each minmax is that column's natural width capped at 116px, so a narrow window
 * squeezes the long text columns first and the whole table still fits ~980px without a scrollbar.
 * The LAST column is fixed px and left alone per page: its contents differ (a 3-dot dropdown, a
 * delete-only button, View + delete) and buttons gain nothing from extra width.
 *
 * Tailwind only emits classes it can find as literal strings, so each page keeps its template as a
 * literal `const COLUMNS`. Do not build one at runtime - the CSS will not exist.
 */
export const HEADER_CELL = 'text-xs font-semibold text-[#64748b] uppercase min-w-0 truncate';

export default HEADER_CELL;
